/**
 * Handwritten TanStack Query hooks wrapping the generated SDK.
 *
 * Pattern:
 *   - One query/mutation hook per endpoint.
 *   - `queryKey` is the hook's stable identifier. Mutations invalidate the
 *     keys whose data they make stale.
 *   - All hooks return the standard TanStack Query result, so consumers can
 *     destructure `{ data, isLoading, error, mutate, ... }` as usual.
 *
 * To add a hook for a new endpoint, follow the existing examples — copy a
 * useQuery wrapper for a GET, a useMutation wrapper for a POST/PUT/DELETE,
 * and call `queryClient.invalidateQueries` for any keys the mutation
 * affects.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  adminActivityActions,
  adminActivityAnalytics,
  adminDeleteUser,
  adminGetUser,
  adminInviteUser,
  adminListActivity,
  adminListUsers,
  adminUpdateUser,
  changePassword,
  controlSim,
  getActivity,
  getHealth,
  getMe,
  getMeDetail,
  getSimHistory,
  getSimState,
  listScenarios,
  login,
  logout,
  resolveNotification,
  setDevice,
  setOccupancy,
  setPreferences,
  setWeather,
  signup,
  type SimControlRequest,
  type SimState,
  updateProfile,
  type WeatherRequest,
} from "@/lib/api/generated";
import { logger } from "@/lib/logger";

// ─── Public queries / mutations ──────────────────────────────────────────

export function useHealth() {
  return useQuery({
    queryKey: ["health"],
    queryFn: async () => {
      const { data } = await getHealth();
      return data;
    },
  });
}

export function useMe() {
  return useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      const { data } = await getMe();
      return data;
    },
    // Don't retry on a 401 — ProtectedRoute will redirect to /login.
    retry: false,
  });
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { email: string; password: string; rememberMe?: boolean }) => {
      const { data } = await login({
        body: {
          email: vars.email,
          password: vars.password,
          remember_me: vars.rememberMe ?? false,
        },
      });
      return data;
    },
    onSuccess: () => {
      // A login can follow a session that ended WITHOUT the in-app logout
      // (expired/cleared cookie), so the cache may still hold the previous
      // identity's data — and a different user logging in on the same tab
      // would briefly see it. Clear everything, mirroring useLogout; the new
      // session refetches what it needs (including "me").
      queryClient.clear();
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await logout();
    },
    onSuccess: () => {
      // Clear the entire cache so no stale per-user data leaks across sessions.
      queryClient.clear();
    },
  });
}

export function useSignup() {
  return useMutation({
    mutationFn: async (vars: {
      email: string;
      password: string;
      firstName: string;
      lastName: string;
    }) => {
      const { data } = await signup({
        body: {
          email: vars.email,
          password: vars.password,
          first_name: vars.firstName,
          last_name: vars.lastName,
        },
      });
      return data;
    },
  });
}

// ─── Profile ─────────────────────────────────────────────────────────────

export function useMeDetail() {
  return useQuery({
    queryKey: ["me", "detail"],
    queryFn: async () => {
      const { data } = await getMeDetail();
      return data;
    },
    retry: false,
  });
}

export function useActivity() {
  return useQuery({
    queryKey: ["activity"],
    queryFn: async () => {
      const { data } = await getActivity();
      return data ?? [];
    },
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { firstName: string; lastName: string }) => {
      await updateProfile({
        body: { first_name: vars.firstName, last_name: vars.lastName },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: async (vars: { currentPassword: string; newPassword: string }) => {
      await changePassword({
        body: { current_password: vars.currentPassword, new_password: vars.newPassword },
      });
    },
  });
}

// ─── Admin ───────────────────────────────────────────────────────────────

export function useAdminUsers() {
  return useQuery({
    queryKey: ["admin", "users"],
    queryFn: async () => {
      const { data } = await adminListUsers();
      return data ?? [];
    },
  });
}

export function useAdminUser(userId: string | null) {
  return useQuery({
    queryKey: ["admin", "users", userId],
    queryFn: async () => {
      if (!userId) {
        return null;
      }
      const { data } = await adminGetUser({ path: { user_id: userId } });
      return data;
    },
    enabled: !!userId,
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      userId: string;
      firstName?: string;
      lastName?: string;
      role?: string;
    }) => {
      await adminUpdateUser({
        path: { user_id: vars.userId },
        body: {
          first_name: vars.firstName,
          last_name: vars.lastName,
          role: vars.role,
        },
      });
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "users", vars.userId] });
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { userId: string }) => {
      await adminDeleteUser({ path: { user_id: vars.userId } });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });
}

export function useInviteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { email: string }) => {
      const { data } = await adminInviteUser({ body: { email: vars.email } });
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });
}

// ─── Admin activity ──────────────────────────────────────────────────────

export interface AdminActivityParams {
  search?: string;
  action?: string;
  userId?: string;
  sortBy?: string;
  sortDir?: "asc" | "desc";
  page?: number;
  pageSize?: number;
}

export function useAdminActivity(params: AdminActivityParams = {}) {
  return useQuery({
    queryKey: ["admin", "activity", params],
    queryFn: async () => {
      const { data } = await adminListActivity({
        query: {
          search: params.search,
          action: params.action,
          user_id: params.userId,
          sort_by: params.sortBy ?? "created_at",
          sort_dir: params.sortDir ?? "desc",
          page: params.page ?? 1,
          page_size: params.pageSize ?? 25,
        },
      });
      return data;
    },
    // Keep the previous page on screen while fetching the next so the table
    // doesn't flash empty between paginations / filter changes.
    placeholderData: (prev) => prev,
  });
}

export function useAdminActivityAnalytics(days = 30) {
  return useQuery({
    queryKey: ["admin", "activity", "analytics", days],
    queryFn: async () => {
      const { data } = await adminActivityAnalytics({ query: { days } });
      return data;
    },
  });
}

export function useAdminActivityActions() {
  return useQuery({
    queryKey: ["admin", "activity", "actions"],
    queryFn: async () => {
      const { data } = await adminActivityActions();
      return data ?? [];
    },
  });
}

// ─── Simulator ───────────────────────────────────────────────────────────
//
// The dashboard polls `sim/state` at ~1 Hz and `sim/history` at ~0.2 Hz.
// Every mutation returns a *fresh* SimState, so instead of invalidating (a
// round-trip that flashes stale data) we write the response straight into the
// ["sim","state"] cache — the whole UI updates in the same tick.

const SIM_STATE_KEY = ["sim", "state"] as const;

export function useSimState() {
  return useQuery({
    queryKey: SIM_STATE_KEY,
    queryFn: async () => {
      const { data } = await getSimState();
      return data;
    },
    refetchInterval: 1000,
    // Keep the last snapshot on screen between polls so the scene never blanks.
    placeholderData: (prev) => prev,
    staleTime: 0,
  });
}

export function useSimHistory(simMinutes = 720, maxPoints = 360) {
  return useQuery({
    queryKey: ["sim", "history", simMinutes, maxPoints],
    queryFn: async () => {
      const { data } = await getSimHistory({
        query: { sim_minutes: simMinutes, max_points: maxPoints },
      });
      return data;
    },
    // Matches the 1 Hz state poll: the payload is a few hundred small numbers,
    // and a slower poll makes the chart lag the clock and lurch on refresh.
    refetchInterval: 1000,
    placeholderData: (prev) => prev,
  });
}

export function useScenarios() {
  return useQuery({
    queryKey: ["sim", "scenarios"],
    queryFn: async () => {
      const { data } = await listScenarios();
      return data ?? [];
    },
    staleTime: Infinity,
  });
}

/**
 * Shared factory for the sim mutations: run the SDK call, then push the fresh
 * SimState it returns directly into the state cache so pollers and the scene
 * pick it up immediately.
 */
function useSimMutation<TVars>(
  label: string,
  call: (vars: TVars) => Promise<SimState | undefined>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: call,
    // Cancel any in-flight 1 Hz poll so it can't resolve *after* the mutation
    // and revert the fresh state we're about to write.
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: SIM_STATE_KEY });
    },
    onSuccess: async (state) => {
      if (state) {
        await queryClient.cancelQueries({ queryKey: SIM_STATE_KEY });
        logger.debug("[sim]", label, "→ applied fresh state");
        queryClient.setQueryData(SIM_STATE_KEY, state);
      }
    },
    onError: (error) => {
      logger.error("[sim]", label, "failed", error);
    },
  });
}

export function useControlSim() {
  return useSimMutation("controlSim", async (vars: SimControlRequest) => {
    const { data } = await controlSim({ body: vars });
    return data;
  });
}

export function useSetWeather() {
  return useSimMutation("setWeather", async (vars: WeatherRequest) => {
    const { data } = await setWeather({ body: vars });
    return data;
  });
}

export function useSetOccupancy() {
  return useSimMutation("setOccupancy", async (vars: { roomId: string; occupants: number }) => {
    const { data } = await setOccupancy({
      body: { room_id: vars.roomId, occupants: vars.occupants },
    });
    return data;
  });
}

export function useSetDevice() {
  return useSimMutation(
    "setDevice",
    // `release: true` hands a smart device back to the MPC (value is ignored);
    // otherwise `value` pins a manual override that persists until released.
    async (vars: { deviceId: string; value?: number; release?: boolean }) => {
      const { data } = await setDevice({
        body: { device_id: vars.deviceId, value: vars.value, release: vars.release },
      });
      return data;
    },
  );
}

export function useResolveNotification() {
  return useSimMutation(
    "resolveNotification",
    async (vars: { id: string; action: "done" | "dismiss" }) => {
      const { data } = await resolveNotification({
        path: { notification_id: vars.id },
        body: { action: vars.action },
      });
      return data;
    },
  );
}

export function useSetPreferences() {
  return useSimMutation(
    "setPreferences",
    async (vars: {
      minComfortTemperature?: number;
      maxComfortTemperature?: number;
      maxCo2?: number;
      minHumidity?: number;
      maxHumidity?: number;
      autoResident?: boolean;
      autoOccupancy?: boolean;
    }) => {
      const { data } = await setPreferences({
        body: {
          min_comfort_temperature: vars.minComfortTemperature,
          max_comfort_temperature: vars.maxComfortTemperature,
          max_co2: vars.maxCo2,
          min_humidity: vars.minHumidity,
          max_humidity: vars.maxHumidity,
          auto_resident: vars.autoResident,
          auto_occupancy: vars.autoOccupancy,
        },
      });
      return data;
    },
  );
}
